import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server.ts'
import {
  Button,
  ButtonRadio,
  Divider,
  Loading,
  message,
  Modal,
  Pagination,
  Trans,
} from '@/components'
import { ActionMenu, ActionMenuItem } from '@/components/base/ActionMenu'
import { EmptyView } from '@/components/EmptyView'
import { ItemConsumeType } from '@/consts/snapshot'
import { useSnapshotStorageTotal } from '@/hooks/useSnapshotStorageTotal'
import { useStorageBillingMode } from '@/hooks/useStorageBillingMode'
import { Snapshot, SnapStatus } from '@/models/snapshot'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { useUserStore } from '@/stores/user'
import { getErrorCodeMessage } from '@/utils/error'
import { formatBytes, formatDatePreset } from '@/utils/formatter'
import {
  getBillingSwitchCompletedAt,
  SWITCH_RESUMABLE_POLL_INTERVAL_MS,
  SWITCH_RESUMABLE_POLL_WINDOW_MS,
} from '@/utils/storage-billing'
import { cn } from '@/utils/style'

import { EditableSnapshotName } from './components/EditableSnapshotName'
import { SnapshotRestoreCounter } from './components/SnapshotRestoreCounter'
import { SnapshotStorageBadge } from './components/SnapshotStorageBadge'

interface FilterItem {
  label: string
  value: string
  codes: number[] | undefined
}

export function SnapshotListPage() {
  const [snapList, setSnapList] = useState<Snapshot[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10
  const navigate = useNavigate()
  const userStore = useUserStore()
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const { t } = useTranslation()
  const [itemConsume, setItemConsume] = useState<{
    total: number
    used: number
  } | null>(null)

  const {
    snapshotOnSpaceBilling,
    loading: billingLoading,
    error: billingError,
  } = useStorageBillingMode()
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)

  const statusFilters: FilterItem[] = [
    {
      label: t('snapshot.type.available'),
      value: 'available',
      codes: [SnapStatus.Finished],
    },
    {
      label: t('snapshot.type.restorable'),
      value: 'restorable',
      codes: [SnapStatus.Deleting, SnapStatus.Resumable],
    },
  ]
  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])

  const notAvailable =
    !billingLoading &&
    !billingError &&
    snapshotOnSpaceBilling &&
    !userStore.partVolume?.quota_gb &&
    snapList?.length === 0

  const getItemConsume = useCallback(async () => {
    const res = await API.getItemConsume({ type: ItemConsumeType.Snapshot })
    if (res.code === 0) {
      setItemConsume(res.info)
    }
  }, [])

  const { snapshotStorageBytes, refresh: getSnapshotStorage } = useSnapshotStorageTotal({
    enabled: !billingLoading && !snapshotOnSpaceBilling,
    domainInfos,
  })

  const getDomains = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos || [])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const getSnapshotList = useCallback(
    async (p = page, silent = false) => {
      if (!silent) setLoading(true)
      try {
        const res = await API.getSnapList({
          domains: domainInfos.map((d) => d.domain),
          page: p,
          per_page: pageSize,
          status: filter?.codes,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          setSnapList(res.snapshots.map((snap) => new Snapshot(snap)))
        }
      } catch (error) {
        console.error(error)
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [page, pageSize, filter, domainInfos],
  )

  useEffect(() => {
    const switchedAt = getBillingSwitchCompletedAt()
    if (!switchedAt || Date.now() - switchedAt >= SWITCH_RESUMABLE_POLL_WINDOW_MS) return

    const intervalId = setInterval(() => {
      if (Date.now() - switchedAt >= SWITCH_RESUMABLE_POLL_WINDOW_MS) {
        clearInterval(intervalId)
        return
      }
      getSnapshotList(page, true)
      getItemConsume()
    }, SWITCH_RESUMABLE_POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [getSnapshotList, getItemConsume, page])

  const onResumeSnapshot = async (resID: string) => {
    const remainingRestores = itemConsume?.total ? itemConsume.total - (itemConsume.used || 0) : 0

    if (!snapshotOnSpaceBilling) {
      Modal.confirm({
        title: t('snapshot.restoreBillingModal.title', 'Restore Snapshot'),
        content: (
          <div className="flex flex-col gap-3">
            <div className="t-lg text-secondary">
              {t(
                'snapshot.restoreBillingModal.content',
                'This snapshot will be restored and time-based billing will resume. Please confirm to proceed.',
              )}
            </div>
            {remainingRestores <= 3 && remainingRestores > 0 && (
              <div className="t-lg text-secondary">
                {remainingRestores === 1 ? (
                  <Trans
                    i18nKey="snapshot.restoreModal.finalRestoreMessage"
                    components={{
                      1: <span className="text-error t-bold" />,
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="snapshot.restoreModal.lowRestoreMessage"
                    values={{ count: remainingRestores }}
                    components={{
                      1: <span className="text-error t-bold" />,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ),
        confirmBtnCfg: {
          type: 'secondary',
          text: t('snapshot.restoreBillingModal.confirmBtn', 'Restore'),
        },
        className: 'w-[640px]',
      }).then(() => {
        performRestore(resID)
      })
      return
    }

    if (remainingRestores <= 3 && remainingRestores > 0) {
      Modal.confirm({
        title: t('snapshot.restoreModal.title'),
        content: (
          <div className="flex-row">
            <div className="t-lg text-secondary">
              {remainingRestores === 1 ? (
                <Trans
                  i18nKey="snapshot.restoreModal.finalRestoreMessage"
                  components={{
                    1: <span className="text-error t-bold" />,
                  }}
                />
              ) : (
                <Trans
                  i18nKey="snapshot.restoreModal.lowRestoreMessage"
                  values={{ count: remainingRestores }}
                  components={{
                    1: <span className="text-error t-bold" />,
                  }}
                />
              )}
            </div>
          </div>
        ),
        confirmBtnCfg: {
          type: 'secondary',
          text: t('snapshot.restoreModal.confirmBtn'),
        },
        className: 'w-[640px]',
      }).then(() => {
        performRestore(resID)
      })
      return
    }

    performRestore(resID)
  }

  const performRestore = async (resID: string) => {
    setLoading(true)
    try {
      const res = await API.resumeSnapshot(
        {
          res_id: resID,
          resume_snapshot: true,
        },
        { errMsg: false },
      )
      if (res.code === 0) {
        message.success(t('snapshot.restoreSuccess', 'Restore request completed'))
        getSnapshotList()
        getItemConsume()
        getSnapshotStorage()
      } else {
        setLoading(false)
        getItemConsume()
        if (res.code === 46) {
          setExchargeModalOpen(true)
        } else if (res.code === 177) {
          Modal.confirm({
            title: t('snapshot.cannotRestoreModal.title'),
            content: (
              <div className="flex-row">
                <div className="t-lg text-secondary">
                  <Trans
                    i18nKey="snapshot.cannotRestoreModal.content"
                    components={{
                      1: <a href="mailto:support@glows.ai" className="text-highlight" />,
                    }}
                  />
                </div>
              </div>
            ),
            confirmBtnCfg: {
              type: 'secondary',
              text: t('snapshot.cannotRestoreModal.confirmBtn'),
            },
            className: 'w-[480px]',
          })
        } else if (snapshotOnSpaceBilling) {
          Modal.confirm({
            title: t('snapshot.restoreFailedModal.title'),
            content: (
              <div className="flex-row">
                <div className="t-lg text-secondary flex items-center">
                  {t('snapshot.restoreFailedModal.content')}
                </div>
              </div>
            ),
            confirmBtnCfg: {
              type: 'secondary',
              text: t('snapshot.restoreFailedModal.confirmBtn'),
            },
            className: 'w-[480px]',
          }).then(() => {
            navigate('/space')
          })
        } else {
          message.error(getErrorCodeMessage(res.code))
        }
      }
    } catch (error) {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDomains()
    getItemConsume()
  }, [])

  useEffect(() => {
    if (domainInfos.length > 0) {
      getSnapshotList(1)
    }
  }, [domainInfos, filter])

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="flex h-[60px] items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h6">
            <Trans i18nKey="snapshot.title">Snapshots</Trans>
          </div>
          <SnapshotRestoreCounter total={itemConsume?.total || 0} used={itemConsume?.used || 0} />
          {!billingLoading && !snapshotOnSpaceBilling && (
            <SnapshotStorageBadge bytes={snapshotStorageBytes} />
          )}
        </div>
        <Link to="/create">
          <Button type="secondary">
            <Trans i18nKey="snapshot.createBtn">New Instance</Trans>
          </Button>
        </Link>
      </div>
      <Divider />

      <ButtonRadio
        value={filter?.value}
        items={statusFilters}
        onChange={(e) => {
          setFilter(statusFilters.find((i) => i.value === e))
        }}
      />

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && !notAvailable && snapList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}

      {!loading && !notAvailable && snapList.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={2}>
                <Trans i18nKey="snapshot.tableHeaderID">ID</Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="snapshot.tableHeaderName">Name</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="snapshot.tableHeaderSize">Size</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="snapshot.tableHeaderStatus">Status</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="snapshot.tableHeaderTime">Create Time</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="snapshot.tableHeaderAction">Action</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {snapList.map((snap) => (
              <tr key={snap.resID} className="group">
                <td colSpan={2} className="t-sm p-4">
                  {snap.resID}
                </td>
                <td colSpan={3} className="p-4">
                  <div className="flex items-center">
                    <EditableSnapshotName snapshot={snap} onUpdate={getSnapshotList} />
                  </div>
                </td>
                <td colSpan={1} className="t-sm p-4">
                  {formatBytes(snap.size)}
                </td>
                <td colSpan={1} className={cn('t-sm p-4', snap.statusText.style)}>
                  {t(snap.statusText.labelKey)}
                </td>
                <td colSpan={2} className="text-tertiary t-sm p-4">
                  {formatDatePreset(snap.createdAt, 'minute')}
                </td>
                <td colSpan={2} className="text-tertiary p-4">
                  {(() => {
                    const isRestorableTab = filter?.value === 'restorable'
                    const handleDelete = () => {
                      Modal.confirm({
                        title: t('snapshot.deleteModal.title'),
                        content: (
                          <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
                            <div className="t-sm font-medium text-secondary flex items-center">
                              {t('snapshot.deleteModal.content')}
                            </div>
                          </div>
                        ),
                        confirmBtnCfg: {
                          type: 'danger',
                          text: t('snapshot.deleteModal.confirmBtn'),
                        },
                        className: 'w-[640px]',
                      }).then(() => {
                        API.deleteSnapshot({ res_id: snap.resID }).then(() => {
                          message.success(t('snapshot.deleteModal.successMsg'))
                          getSnapshotList()
                          getSnapshotStorage()
                        })
                      })
                    }

                    const items: ActionMenuItem[] = isRestorableTab
                      ? [
                          {
                            label: <Trans i18nKey="snapshot.actionRestore">Restore</Trans>,
                            onClick: () => onResumeSnapshot(snap.resID),
                          },
                          {
                            label: (
                              <Trans i18nKey="snapshot.actionPermanentDelete">
                                Permanently Delete
                              </Trans>
                            ),
                            onClick: handleDelete,
                            type: 'danger',
                          },
                        ]
                      : [
                          {
                            label: <Trans i18nKey="snapshot.actionDelete">Delete</Trans>,
                            onClick: handleDelete,
                            type: 'danger',
                          },
                        ]

                    return <ActionMenu items={items} popupContainer="body" />
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && !notAvailable && snapList.length > 0 && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getSnapshotList(page)}
        />
      )}
      {exchargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setExchargeModalOpen(false)
            userStore.getAccount()
            getSnapshotList()
            getItemConsume()
          }}
          onClose={() => {
            setExchargeModalOpen(false)
          }}
        />
      )}

      {!loading && notAvailable && (
        <div className="mt-4 mb-2 ">
          <EmptyView
            title={
              <Trans i18nKey="snap.insufficientQuotaAvailableTitle">
                Insufficient quota available
              </Trans>
            }
            content={
              <Trans i18nKey="snap.insufficientQuotaAvailableContent">
                Update your quota settings in Space Management to proceed
              </Trans>
            }
            action={
              <Button type="secondary" onClick={() => navigate('/space')}>
                <Trans i18nKey="snapshots.emptyViewActionBtn">Manage Space</Trans>
              </Button>
            }
          />
        </div>
      )}
    </div>
  )
}
